import { motion } from "framer-motion";
import {
    Crown,
    Baby,
    HeartHandshake,
    Wrench,
    Waves,
    Scissors,
    Gem,
    ArrowUpRight,
} from "lucide-react";

const ease = [0.76, 0, 0.24, 1];

const services = [
    {
        n: "01",
        title: "Dental Implants",
        blurb: "Titanium roots, permanent smiles — osseointegrated precision from a certified oral implantologist.",
        Icon: Gem,
        tags: ["Single-tooth", "Full-arch", "Digital planning"],
    },
    {
        n: "02",
        title: "Periodontology",
        blurb: "Advanced gum therapy — scaling, root planing, flap surgery and tissue regeneration.",
        Icon: HeartHandshake,
        tags: ["Deep cleaning", "Gum surgery", "Regeneration"],
    },
    {
        n: "03",
        title: "Root Canal",
        blurb: "Modern, mostly single-sitting endodontics. Quiet chair, quicker recovery.",
        Icon: Wrench,
        tags: ["Single sitting", "Rotary endo", "Post-care"],
    },
    {
        n: "04",
        title: "Prosthesis",
        blurb: "Crowns, bridges and dentures — replacing missing teeth to restore bite, function and confidence.",
        Icon: Crown,
        tags: ["Crowns & bridges", "Dentures", "Implant-supported"],
    },
    {
        n: "05",
        title: "Scaling & Polishing",
        blurb: "Ultrasonic prophylaxis and polish — the six-monthly ritual your teeth deserve.",
        Icon: Waves,
        tags: ["Ultrasonic", "Stain removal"],
    },
    {
        n: "06",
        title: "Extractions & Wisdom Tooth",
        blurb: "Atraumatic surgical and wisdom-tooth removal, with detailed post-op care.",
        Icon: Scissors,
        tags: ["Surgical", "Wisdom molar", "Home protocol"],
    },
    {
        n: "07",
        title: "Kids Dentistry",
        blurb: "Gentle paediatric care — first visits, fluoride, sealants, and calm conversations.",
        Icon: Baby,
        tags: ["First visits", "Sealants", "Habit counselling"],
    },
    {
        n: "08",
        title: "Cosmetic Dentistry",
        blurb: "Veneers, bonding, contouring and full smile design — quietly transformative.",
        Icon: Gem,
        tags: ["Veneers", "Bonding", "Smile design"],
    },
];

const Row = ({ s, i }) => {
    const Icon = s.Icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8%" }}
            transition={{ duration: 0.8, delay: (i % 4) * 0.05, ease }}
            className="group relative border-t border-surya-line last:border-b py-8 md:py-10 grid grid-cols-12 gap-4 md:gap-8 items-center cursor-pointer overflow-hidden"
            data-testid={`service-card-${s.n}`}
        >
            {/* orange sweep on hover */}
            <div className="pointer-events-none absolute inset-0 bg-surya-orange origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)]" />

            <div className="relative col-span-2 md:col-span-1 chapter-num text-2xl md:text-3xl text-surya-muted group-hover:text-white/70">
                /{s.n}
            </div>
            <div className="relative col-span-10 md:col-span-4">
                <h3 className="font-serif text-surya-ink group-hover:text-white text-2xl md:text-4xl font-light tracking-[-0.02em] leading-[0.98]">
                    {s.title.split(" ").map((w, k) => (
                        <span
                            key={k}
                            className={
                                k === s.title.split(" ").length - 1
                                    ? "italic-display text-surya-orange group-hover:text-white/95"
                                    : ""
                            }
                        >
                            {w}{" "}
                        </span>
                    ))}
                </h3>
            </div>
            <div className="relative hidden md:block col-span-5 text-surya-muted group-hover:text-white/85 text-sm md:text-base leading-relaxed">
                {s.blurb}
                <div className="mt-3 flex flex-wrap gap-2">
                    {s.tags.map((t, k) => (
                        <span
                            key={k}
                            className="inline-block label text-surya-muted group-hover:text-white/70 border border-surya-line group-hover:border-white/30 px-2.5 py-1 rounded-full"
                        >
                            {t}
                        </span>
                    ))}
                </div>
            </div>
            <div className="relative col-span-12 md:col-span-2 flex md:justify-end items-center gap-3">
                <div className="hidden md:block h-11 w-11 border border-surya-line group-hover:border-white/40 rounded-full flex items-center justify-center overflow-hidden">
                    <Icon
                        className="h-5 w-5 text-surya-ink group-hover:text-white transition-transform duration-500 group-hover:rotate-45"
                        strokeWidth={1}
                    />
                </div>
                <ArrowUpRight
                    className="h-5 w-5 text-surya-ink group-hover:text-white transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.4}
                />
            </div>
        </motion.div>
    );
};

export const Services = () => {
    return (
        <section
            id="expertise"
            className="py-24 md:py-40 px-6 md:px-10 bg-surya-bg relative z-[2]"
            data-testid="services-section"
        >
            <div className="mx-auto max-w-[1600px]">
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20 items-end">
                    <div className="col-span-12 md:col-span-2">
                        <div className="label text-surya-muted">Chapter</div>
                        <div className="chapter-num text-[3rem] md:text-[4.5rem] leading-none text-surya-ink mt-1">
                            02<span className="text-surya-orange">.</span>
                        </div>
                        <div className="label text-surya-muted">The Expertise</div>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                        <h2 className="font-serif text-surya-ink text-[clamp(2rem,4.5vw,4rem)] leading-[0.95] tracking-[-0.02em] font-light">
                            Eight ways we care —{" "}
                            <span className="italic-display text-surya-orange">
                                one philosophy.
                            </span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-3 md:text-right">
                        <p className="text-surya-muted text-sm max-w-xs md:ml-auto">
                            From routine cleanings to full-arch implant
                            rehabilitation — every procedure grounded in
                            evidence, delivered with warmth.
                        </p>
                    </div>
                </div>

                <div>
                    {services.map((s, i) => (
                        <Row key={s.n} s={s} i={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};
